import distinctColors from 'distinct-colors';

const colors = ['rgb(132, 186, 91)', 'rgb(211, 94, 96)', 'rgb(114, 147, 203)', 'rgb(225, 151, 76)', 'rgb(128, 133, 133)', 'rgb(144, 103, 167)', 'rgb(171, 104, 87)', 'rgb(204, 194, 16)'];
const getBarColor = (shouldAggregate, index, colors, isPositive, count) => {
    if (!shouldAggregate) {
        return colors[isPositive ? 0 : 1];
    }

    if (count <= 2) {
        return colors[index % 2];
    }

     return colors[index];
};

const getSeries = dataList => dataList
    .reduce((acc, val) => [...acc, ...(val.chartBlocks || [])], [])
    .map(b => b.title)
    .reduce((acc, val) => {
        if (acc.indexOf(val) === -1) {
            return [...acc, val];
        }
        return acc;
    }, []);

export const getLineDataset = (data, index, projects, groups, shouldAggregate) => {
    const { graphData } = data;
    if (!graphData || graphData.length === 0 || graphData.every(gd => !gd.lineGraphData)) {
        return;
    }

    const transformed = graphData.map(d => {
        const dataSource = d.graphRelatedObjectType === 'project' ? projects : groups;
        const dataItem = dataSource.find(ds => ds.id === d.graphRelatedObjectId);
        if (!dataItem) {
            return;
        }
        let points;
        if (!shouldAggregate) {
            const lineData = d.lineGraphData[index];
            points = lineData ? lineData.data : [];
        } else {
            if (index === 0) {
                points = d.lineGraphData.map(lgd => lgd.data).reduce((acc, val) => {
                    for (let i = 0; i < val.length; i++) {
                        if(acc[i] !== undefined) {
                            acc[i] = acc[i] + val[i];
                        } else {
                            acc[i] = val[i];
                        }
                    }
                    return acc;
                }, []);
            } else {                
                const lineData = d.lineGraphData[index - 1];
                points = lineData ? lineData.data : [];
            }
        }

        const stroke = `rgba(${dataItem.color.r}, ${dataItem.color.g}, ${dataItem.color.b}, 0.8)`;
        return {
            label: dataItem.name,
            borderColor: stroke,
            pointBackgroundColor: stroke,
            pointBorderColor: stroke,
            pointHoverBackgroundColor: stroke,
            pointHoverBorderColor: stroke,
            data: points
        };
    }).reduce((acc, val) => {
        if (!val) {
            return acc;
        }
        const { borderColor, pointBackgroundColor, pointBorderColor, pointHoverBackgroundColor, pointHoverBorderColor } = val;
        const colorObj = {
            borderColor,
            pointBackgroundColor,
            pointBorderColor,
            pointHoverBackgroundColor,
            pointHoverBorderColor
        };
        return {
            colors: [...acc.colors, colorObj],
            data: [...acc.data, val.data],
            series: [...acc.series, val.label]
        };
    }, { colors: [], data: [], series: [] });
    return {
        ...data,
        graphData: transformed
    };
};

const transformBarData = (graphData, projects, groups) => {
    const filteredData = graphData.reduce((acc, val) => {
        const blocks = val.chartBlocks;
        const dataSource = val.graphRelatedObjectType === 'project' ? projects : groups;
        const dataItem = dataSource.find(d => d.id === val.graphRelatedObjectId);
        if (!dataItem) {
            return acc;
        }
        const metaText = dataItem.name;
        const color = dataItem.color;
        return [
            ...acc,
            ...blocks.map(b => ({
                metaText,
                color,
                title: b.title,
                value: b.value,
                isAggregate: b.isAggregate
            }))
        ];
    }, []);

    const transformed = filteredData.reduce((acc, val) => {
        const labels = acc.labels.indexOf(val.metaText) === -1 ? [...acc.labels, val.metaText] : acc.labels;
        const present = acc.override.find(o => o.label === val.title);
        let override = acc.override;
        if (present) {
            present.data = [...present.data, val.value];
        } else {
            override = [...override, { label: val.title, data: [val.value], color: val.color, isAggregate: val.isAggregate }];
        }

        const metaColors = { ...acc.metaColors, [val.metaText]: val.color };

        return {
            metaColors,            
            labels,
            override,
            series: [...acc.series, val.title]
        };
    }, {
            metaColors: {},
            labels: [],
            series: [],
            override: []
        });

    return {
        ...transformed,
        series: transformed.series.reduce((acc, val) => {
            if (acc.indexOf(val) === -1) {
                return [
                    ...acc,
                    val
                ];
            }
            return acc;
        }, [])
    }
};

export const getBarDataset = (data, summary, projects, groups) => {
    const { graphData } = data;
    if (!graphData || graphData.length === 0 || graphData.every(gd => !gd.chartBlocks)) {
        return;
    }

    const shouldAggregate = graphData.find(gd => gd.chartBlocks.some(cb => cb.title.toLowerCase() === summary.title.toLowerCase() && cb.isAggregate));

    const transformed = transformBarData(graphData, projects, groups);    

    const unsortedOverride = transformed.override.reduce((acc, val) => {
        return [
            ...acc,
            ...val.data.map((d, i, arr) => ({
                ...val,
                data: arr.map((dt, idx) => idx === i ? dt : 0),
                stack: transformed.labels[i],
                label: `${val.label}-${transformed.labels[i]}`,
                serie: val.label
            }))
        ];
    }, []);

    const sortedOverride = transformed.labels.reduce((acc, val) => {
        const filtered = unsortedOverride.filter(uo => uo.stack === val);
        return [
            ...acc,
            ...filtered
        ];
    }, []);
    
    const count = shouldAggregate ? transformed.series.length - 1 : 1;
    const dColors = count <= 8 ? colors : distinctColors({ count });

    if (shouldAggregate) {
        const aggregatedData = {
            ...transformed,
            series: transformed.series.filter(s => s !== summary.title),
            override: sortedOverride.filter(o => o.serie.toLowerCase() !== summary.title.toLowerCase()).map((o, i) => {
                const color = getBarColor(shouldAggregate, i, dColors, summary.isPositive, count);
                return {
                    ...o,
                    color: transformed.metaColors[o.stack],
                    borderColor: color,
                    backgroundColor: color
                };
            })
        };
        return {
            ...data,
            graphData: {
                ...aggregatedData,
                data: aggregatedData.override.map(o => o.data),
                colors: aggregatedData.metaColors
            }
        };
    } else {
        const serie = transformed.series.find(s => s.toLowerCase() === summary.title.toLowerCase());
        const transformedData = {
            labels: transformed.labels,
            series: [serie],
            override: sortedOverride.filter(o => o.serie === serie).map((o, i) => {
                // const color = getBarColor(shouldAggregate, i, dColors, summary.isPositive, count);
                const colorRgbCss =`rgba(${transformed.metaColors[o.stack].r},${transformed.metaColors[o.stack].g},${transformed.metaColors[o.stack].b},0.8)`;
                return {
                    ...o,
                    color: transformed.metaColors[o.stack],
                    borderColor: colorRgbCss,
                    backgroundColor: colorRgbCss
                };
            }),
            colors: transformed.metaColors
        };
        return {
            ...data,
            graphData: {
                ...transformedData,
                data: transformedData.override.map(t => t.data)
            }
        };
    }
}

const getContentHeight = (display, width, type) => {
    switch (display) {
        case 'all':
            return 400;
        case 'graph':
            return 370;
        case 'summary':
            return type === 'deploymentFrequency' ? 310 : 334;
        default:
            return 0;
    }
}

export const getViewInfo = (display, type) => {
    return {
        contentHeight: getContentHeight(display, window.innerWidth, type),
        graph: display === 'all' || display === 'graph',
        summaries: display === 'all' || display === 'summary',
        visible: display !== 'none'
    };
};

export const shouldAggregate = (data, summaries) => {
    if (!data) {
        return false;
    }

    const { graphData } = data.toJS ? data.toJS() : data;
    if (!graphData) {
        return false;
    }

    if (graphData.some(d => !!d.chartBlocks)) {
        const series = getSeries(graphData);
        return series.length < summaries.length;
    } else if (graphData.some(d => !!d.projects)) {
        const series = getSeries(graphData[0].projects);
        return series.length < summaries.length;
    } else if (graphData.some(d => !!d.lineGraphData)) {
        return graphData[0].lineGraphData.length < summaries.length;
    }

    return false;
};

export const specificOrFirst = (list, index) => list.length > index && index > -1 ? list[index] : list[0];

const transformGroupedData = (graphData, projects, groups, environmentTypes) => {
    const filteredData = graphData.map(gd => ({
        env: environmentTypes.find(env => env.id === gd.id) ? environmentTypes.find(env => env.id === gd.id).name : '',
        data: gd.projects.length === 0 ? [] : gd.projects.reduce((acc, val) => {
            const blocks = val.chartBlocks;
            const dataSource = val.graphRelatedObjectType === 'project' ? projects : groups;
            const dataItem = dataSource.find(d => d.id === val.graphRelatedObjectId);
            if (!dataItem) {
                return acc;
            }
            const stack = dataItem.name;
            const color = dataItem.color;
            return [
                ...acc,
                ...blocks.map(b => ({
                    stack,
                    color,
                    title: b.title + ' - ' + stack,
                    value: b.value,
                    serie: b.title,
                    isAggregate: b.isAggregate
                }))
            ];
        }, [])
    }));

    const transformed = filteredData.reduce((acc, val) => {
        const labels = [...acc.labels, val.env];
        const override = val.data
            .map(d => {
                const present = acc.override.find(o => o.label === d.title && o.stack === d.stack);
                return present
                    ? { ...present, data: [...present.data, d.value] }
                    : { label: d.title, stack: d.stack, data: [d.value], color: d.color, isAggregate: d.isAggregate, serie: d.serie };
            });
        return {
            labels,
            override,
            series: acc.series.length > 0 ? acc.series : override.map(o => o.label)
        };
    }, {
            labels: [],
            series: [],
            override: []
        });
    return {
        ...transformed,
        series: transformed.series.reduce((acc, val) => {
            if (acc.indexOf(val) === -1) {
                return [
                    ...acc,
                    val
                ];
            }
            return acc;
        }, [])
    }
};

export const getGroupedDataset = (data, summary, projects, groups, environmentTypes) => {
    const { graphData } = data;
    if (!graphData) {
        return;
    }

    if (graphData.length === 0) {
        return [];
    }

    if (graphData.every(gd => !gd.projects || gd.projects.every(p => !p.chartBlocks))) {
        return;
    }

    const shouldAggregate = graphData[0].projects.length > 0 ? graphData[0].projects[0].chartBlocks.some(b => b.title === summary.title && b.isAggregate) : false;
    const count = shouldAggregate ? graphData[0].projects[0].chartBlocks.length - 1 : 1;
    const dColors = count <= 8 ? colors : distinctColors({ count });
    const transformed = transformGroupedData(graphData, projects, groups, environmentTypes);
    if (shouldAggregate) {
        const aggregatedData = {
            labels: transformed.labels,
            series: transformed.series.filter(s => s.toLowerCase().indexOf(summary.title.toLowerCase()) !== -1),
            override: transformed.override.filter(o => o.serie.toLowerCase() !== summary.title.toLowerCase()).map((o, i) => ({
                ...o,
                borderColor: getBarColor(shouldAggregate, i, dColors, summary.isPositive, count),
                backgroundColor: getBarColor(shouldAggregate, i, dColors, summary.isPositive, count)
            }))
        };
        return {
            ...data,
            graphData: {
                ...aggregatedData,
                data: aggregatedData.override.map(t => t.data)
            }
        };
    } else {
        const transformedData = {
            labels: transformed.labels,
            series: [summary.title],
            override: transformed.override.filter(o => o.serie.toLowerCase() === summary.title.toLowerCase()).map((o, i) => {
                const color = getBarColor(shouldAggregate, i, dColors, summary.isPositive, count); 
                return {
                    ...o,
                    borderColor: color,
                    backgroundColor: color
                };
            })
        };
        return {
            ...data,
            graphData: {
                ...transformedData,
                data: transformedData.override.map(t => t.data)
            }
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/authorized/newDashBoard/selectors/helpers.js